import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Fade,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, Receipt as ReceiptIcon, Save as SaveIcon } from '@mui/icons-material';
import { getTransactionByBookingId, getFactusUserDataByUserId } from '../../services/facturationService';
import { triggerEndCharge, updateBookingCost } from '../../services/bookingService';
import { PowerOff as PowerOffIcon } from '@mui/icons-material';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';

const FacturacionModal = ({ isOpen, onClose, reserva }) => {
  const navigate = useNavigate();
  const [cost, setCost] = useState(0);
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasFacturationData, setHasFacturationData] = useState(false);

  useEffect(() => {
    if (isOpen && reserva) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const trans = await getTransactionByBookingId(reserva.booking_id);
          setTransaction(trans);
          setCost(trans ? trans.amount : reserva.total_cost);
          
          if (trans && reserva.user_id) {
            const facturationData = await getFactusUserDataByUserId(reserva.user_id);
            setHasFacturationData(!!facturationData);
          } else {
            setHasFacturationData(false);
          }
        } catch (error) {
          setTransaction(null);
          setCost(reserva.total_cost);
          setHasFacturationData(false);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [isOpen, reserva]);

  if (!reserva) {
    return null;
  }

  const handleViewFactura = async () => {
    setIsDownloading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${api.defaults.baseURL}/facturation/booking/${reserva.booking_id}/invoice`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `factura_${reserva.booking_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error al descargar la factura:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSaveCost = async () => {
    setIsSaving(true);
    try {
      await updateBookingCost(reserva.booking_id, cost);
      onClose(true);
    } catch (error) {
      console.error("Error al actualizar el costo:", error);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleEndCharge = async () => {
    try {
      await triggerEndCharge(reserva.booking_id);
      onClose(true);
    } catch (error) {
      console.error("Error al finalizar la carga:", error);
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose} closeAfterTransition>
      <Fade in={isOpen}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: '70%', md: '500px' },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="h2">
              Detalle de la Transacción
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                {loading ? (
                  <Grid item xs={12}>
                    <Typography>Cargando...</Typography>
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body1">
                        <strong>Reserva ID:</strong> {reserva.booking_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1">
                        <strong>Usuario:</strong> {reserva.username}
                      </Typography>
                    </Grid>
                    {transaction && (
                      <Grid item xs={12}>
                        <Typography variant="body1">
                          <strong>Transacción ID:</strong> {transaction.id}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Typography variant="body1">
                        <strong>Costo Total:</strong>
                      </Typography>
                      <TextField
                        type="number"
                        value={cost}
                        onChange={(e) => setCost(e.target.value)}
                        fullWidth
                        size="small"
                        disabled={new Date(reserva.end_date) > new Date()}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {new Date(reserva.end_date) < new Date() && !transaction && (
              <Button
                variant="contained"
                startIcon={<PowerOffIcon />}
                onClick={handleEndCharge}
                color="secondary"
              >
                Finalizar Carga
              </Button>
            )}
            {new Date(reserva.end_date) < new Date() && transaction && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveCost}
                color="primary"
                disabled={isSaving}
              >
                {isSaving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
            )}
            {transaction && hasFacturationData && (
              <Button
                variant="contained"
                startIcon={<ReceiptIcon />}
                onClick={handleViewFactura}
                disabled={isDownloading}
              >
                {isDownloading ? <CircularProgress size={24} /> : 'Ver Factura'}
              </Button>
            )}
          </Box>
          {!hasFacturationData && transaction && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              El usuario debe ingresar sus datos de facturación para poder generar la factura electronica.
            </Alert>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default FacturacionModal;